import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { integrationApi, onboardingApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { StatusPill } from '@/components/ui/StatusPill'
import { RadioCard, Switch, YesNo } from '@/components/ui/Toggle'
import { NaukriConnectModal } from '@/features/onboarding/NaukriConnectModal'
import { useIsPhone } from '@/features/profile/shared'
import { useLicense } from '@/features/billing/useLicense'
import { isLicenseError } from '@/store/license'
import { cn, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { ApplicationEmailMode, ResumeOptimization } from '@/types/api'

import { AddressPill, ApplicationEmailSettings, EmailIntegrationsSettings, SettingRow } from './MailSettings'

/** `application_settings.answer_mode`: what happens to a required question the profile cannot answer. */
const ANSWER_MODES = [
  {
    value: 'honest' as const,
    title: 'Ask me when unsure',
    description: 'Answers only what your profile supports. Anything else is asked of you (or an admin) before the application is sent.',
  },
  {
    value: 'aggressive' as const,
    title: 'Fill more automatically',
    description: 'Uses your profile and suggested answers to complete more questions. Personal declarations still require confirmed information.',
  },
]

const OPTIMIZATION = [
  {
    value: 'off' as const,
    title: 'Keep my original resume',
    description: 'Send your resume exactly as uploaded.',
  },
  {
    value: 'honest' as const,
    title: 'Highlight relevant experience',
    description: "Reorder and emphasize experience that's relevant to each job.",
  },
  {
    value: 'aggressive' as const,
    title: 'Tailor the wording',
    description: 'Adapt wording to the job description while keeping your experience accurate.',
  },
]

const EMAIL_MODES: Array<{
  value: ApplicationEmailMode
  title: string
  description: string
}> = [
  {
    value: 'plain',
    title: 'Use my Gmail as-is (recommended)',
    description:
      'Employers get the mailbox you connected, exactly as it is; their mail lands in your inbox as usual.',
  },
  {
    value: 'plus_tag',
    title: 'Use a +jobs address (separate portal accounts)',
    description: 'Same inbox, but employer portals see a new address, so we create fresh accounts there.',
  },
  {
    value: 'alias',
    title: 'Use my JobSlide alias',
    description:
      'A private address we manage: codes and links are handled for you and replies are filed in your Inbox.',
  },
]

/** The ids are anchors other screens link to (`/settings#application-email`); they do not change. */
const SECTIONS = [
  { id: 'submitting', label: 'Applying', description: 'Guardrails for applying on your behalf.' },
  { id: 'resume-optimization', label: 'Resume', description: 'How much we tailor your resume for each job.' },
  {
    id: 'application-email',
    label: 'Email',
    description: 'The address we apply with, so verification mail is handled for you.',
  },
  { id: 'email-integrations', label: 'Mailboxes', description: 'Connect the mailbox you already apply from.' },
  { id: 'job-boards', label: 'Naukri', description: 'Where we pull roles into your feed from.' },
  {
    id: 'application-password',
    label: 'Portal password',
    description:
      'The one password we use whenever a job site makes you an account to apply — Workday, iCIMS, Oracle, SuccessFactors, Taleo and the rest. Set once, used everywhere, encrypted at rest.',
  },
] as const

type SectionId = (typeof SECTIONS)[number]['id']
const FIRST_SECTION: SectionId = SECTIONS[0].id
const isSectionId = (value: string): value is SectionId => SECTIONS.some((section) => section.id === value)

export function SettingsPage() {
  const queryClient = useQueryClient()
  // Auto Apply is a Power feature: switching it on without Power opens the upgrade dialog.
  const { requireFeature } = useLicense()
  const phone = useIsPhone()
  const [connectOpen, setConnectOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  // `?connect=naukri` (linked from a failed application) opens the dialog directly.
  useEffect(() => {
    if (searchParams.get('connect') !== 'naukri') return
    setConnectOpen(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  // Gmail / Outlook OAuth comes back to `?mail=connected|error`.
  const mailResult = searchParams.get('mail')
  useEffect(() => {
    if (!mailResult) return
    if (mailResult === 'connected') {
      toast.success('Mailbox connected', "We'll start sorting employer replies into your Inbox.")
      queryClient.invalidateQueries({ queryKey: ['mail-accounts'] })
    } else if (searchParams.get('reason') === 'gmail_permission') {
      toast.error(
        'Gmail permission was not ticked',
        'Connect again and tick "View your email messages and settings" on Google\'s screen — we need it to read verification emails.',
      )
    } else if (searchParams.get('reason') === 'declined') {
      toast.error('Mailbox not connected', 'The sign-in was cancelled.')
    } else {
      toast.error('Could not connect your mailbox', 'Please try again.')
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('mail')
        next.delete('reason')
        next.delete('provider')
        return next
      },
      { replace: true },
    )
  }, [mailResult, queryClient, setSearchParams])

  const settingsQuery = useQuery({
    queryKey: ['application-settings'],
    queryFn: onboardingApi.getSettings,
  })
  const naukriQuery = useQuery({
    queryKey: ['integrations', 'naukri'],
    queryFn: integrationApi.getNaukri,
  })

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      onboardingApi.saveSettings({ ...settingsQuery.data!, ...patch }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['application-settings'], updated)
      toast.success('Settings saved')
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not save', error.message)
    },
  })

  const sync = useMutation({
    mutationFn: () => integrationApi.sync(30),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['top-matches'] })
      toast.success(`Imported ${result.jobsImported} job(s)`, `Found ${result.jobsFound} in total.`)
    },
    onError: (error: ApiError) => toast.error('Sync failed', error.message),
  })

  const disconnect = useMutation({
    mutationFn: () => integrationApi.disconnect('naukri'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      toast.info('Naukri disconnected')
    },
  })

  // Phones: an accordion, the first section open; a hash opens that section instead.
  const { hash } = useLocation()
  const hashId = hash.slice(1)
  const [open, setOpen] = useState<SectionId>(isSectionId(hashId) ? hashId : FIRST_SECTION)
  useEffect(() => {
    if (isSectionId(hashId)) setOpen(hashId)
  }, [hashId])

  // In-page anchors (e.g. /settings#application-email) only exist once loaded.
  const loaded = Boolean(settingsQuery.data)
  useEffect(() => {
    if (!loaded || !hash) return
    const timer = window.setTimeout(
      () => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }),
      40,
    )
    return () => window.clearTimeout(timer)
  }, [loaded, hash, phone])

  // Desktop: the anchor list follows the section in view.
  const [active, setActive] = useState<SectionId>(FIRST_SECTION)
  useEffect(() => {
    if (!loaded || phone || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (visible && isSectionId(visible.target.id)) setActive(visible.target.id)
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: 0 },
    )
    for (const section of SECTIONS) {
      const node = document.getElementById(section.id)
      if (node) observer.observe(node)
    }
    return () => observer.disconnect()
  }, [loaded, phone])

  if (settingsQuery.isLoading) return <PageSpinner label="Loading settings" />
  const settings = settingsQuery.data
  if (!settings) return null

  const naukri = naukriQuery.data

  const bodies: Record<SectionId, ReactNode> = {
    submitting: (
      <>
        <p className="mt-4 mb-2 text-[13px] font-medium text-ink">Answering screening questions</p>
        <div role="radiogroup" aria-label="Answer mode" className="panel mb-4 overflow-hidden">
          {ANSWER_MODES.map((option, index) => (
            <RadioCard
              key={option.value}
              selected={(settings.answerMode ?? 'honest') === option.value}
              onSelect={() => save.mutate({ answerMode: option.value })}
              title={option.title}
              description={option.description}
              className={index > 0 ? 'border-t border-line' : ''}
            />
          ))}
        </div>
        <SettingRow label="Review before submit" hint="Pause on a review screen so you can check each application first.">
          <YesNo
            value={settings.reviewBeforeSubmit}
            onChange={(value) => save.mutate({ reviewBeforeSubmit: value })}
            aria-label="Review before submit"
          />
        </SettingRow>
        <SettingRow label="Auto apply" hint="Apply automatically to strong matches.">
          <Switch
            checked={settings.autoApplyEnabled}
            onChange={(value) =>
              value
                ? requireFeature('autoApply', () => save.mutate({ autoApplyEnabled: true }))
                : save.mutate({ autoApplyEnabled: false })
            }
            label="Auto apply"
          />
        </SettingRow>
        <SettingRow
          label="Daily application limit"
          hint="Resets at midnight in your schedule’s time zone. Start with 30–50; up to 1,000 per day, subject to your plan. Queued automatic runs wait when you pause or reach the limit."
          htmlFor="setting-daily-limit"
        >
          <input
            id="setting-daily-limit"
            type="number"
            min={1}
            max={1000}
            defaultValue={settings.dailyApplicationLimit}
            onBlur={(event) => {
              const value = Number(event.target.value)
              if (value !== settings.dailyApplicationLimit) save.mutate({ dailyApplicationLimit: value })
            }}
            className={NUMBER_INPUT}
          />
        </SettingRow>
        <SettingRow
          label="Minimum match score"
          hint="Auto apply skips anything below this."
          htmlFor="setting-min-score"
        >
          <input
            id="setting-min-score"
            type="number"
            min={0}
            max={100}
            defaultValue={settings.minMatchScore}
            onBlur={(event) => {
              const value = Number(event.target.value)
              if (value !== settings.minMatchScore) save.mutate({ minMatchScore: value })
            }}
            className={NUMBER_INPUT}
          />
        </SettingRow>
      </>
    ),
    'resume-optimization': (
      <>
        <div role="radiogroup" aria-label="Resume optimization" className="panel my-4 overflow-hidden">
          {OPTIMIZATION.map((option, index) => (
            <RadioCard
              key={option.value}
              selected={settings.resumeOptimization === option.value}
              onSelect={() => save.mutate({ resumeOptimization: option.value as ResumeOptimization })}
              title={option.title}
              description={option.description}
              className={index > 0 ? 'border-t border-line' : ''}
            />
          ))}
        </div>
        <SettingRow label="Auto-approve edits" hint="Skip the preview step and send optimized files straight through.">
          <YesNo
            value={settings.autoApproveEdits}
            onChange={(value) => save.mutate({ autoApproveEdits: value })}
            aria-label="Auto-approve edits"
          />
        </SettingRow>
        <SettingRow label="Generate cover letters" hint="Written from your profile whenever a job asks for one.">
          <YesNo
            value={settings.generateCoverLetter}
            onChange={(value) => save.mutate({ generateCoverLetter: value })}
            aria-label="Generate cover letters"
          />
        </SettingRow>
      </>
    ),
    'application-email': (
      <>
        <div role="radiogroup" aria-label="Application email" className="panel my-4 overflow-hidden">
          {EMAIL_MODES.filter((option) => !settings.applicationEmailLocked || option.value === 'alias').map(
            (option, index) => {
              const unavailable = option.value === 'alias' && !settings.aliasAvailable
              return (
                <RadioCard
                  key={option.value}
                  selected={settings.applicationEmailMode === option.value}
                  disabled={unavailable}
                  onSelect={() => {
                    if (settings.applicationEmailMode !== option.value) {
                      save.mutate({ applicationEmailMode: option.value })
                    }
                  }}
                  title={
                    option.value === 'plus_tag'
                      ? `Use a +${settings.applicationEmailPlusTag} address (separate portal accounts)`
                      : option.title
                  }
                  description={
                    unavailable ? "Not available yet: the mail domain isn't configured." : option.description
                  }
                  className={index > 0 ? 'border-t border-line' : ''}
                />
              )
            },
          )}
        </div>
        {/* The alias block below shows its own address; say it once. */}
        {settings.applicationEmail && !(settings.applicationEmailMode === 'alias' && settings.aliasAvailable) && (
          <div className="flex flex-col gap-2 pb-4 sm:flex-row sm:items-center sm:gap-3">
            <p className="shrink-0 text-[13px] text-ink-muted">Employers will see</p>
            <AddressPill address={settings.applicationEmail} className="max-w-full" />
          </div>
        )}
        {settings.applicationEmailMode === 'alias' && settings.aliasAvailable && <ApplicationEmailSettings />}
      </>
    ),
    'email-integrations': <EmailIntegrationsSettings />,
    'job-boards': (
      <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden
            className="grid h-10 w-10 shrink-0 place-items-center rounded-[9px] border border-line bg-surface-muted text-[14px] font-semibold text-ink-soft"
          >
            N
          </span>
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 text-[15px] font-medium text-ink">
              Naukri
              {naukri?.needsReconnect ? (
                <StatusPill size="sm" tone="warning">
                  Reconnect needed
                </StatusPill>
              ) : naukri?.isConnected ? (
                <StatusPill size="sm" tone="success">
                  Connected
                </StatusPill>
              ) : naukri?.status === 'error' ? (
                <StatusPill size="sm" tone="danger">
                  Error
                </StatusPill>
              ) : (
                <StatusPill size="sm">Not connected</StatusPill>
              )}
            </p>
            <p className="mt-0.5 break-words text-[13px] leading-relaxed text-ink-muted">
              {naukri?.needsReconnect ? (
                'This connection was made in demo mode. Sign in again to pull and apply to real Naukri jobs.'
              ) : naukri?.isConnected ? (
                <>
                  {naukri.accountIdentifier ?? ''} · <span className="num">{naukri.jobsSynced}</span> jobs synced ·
                  last <span className="num">{relativeTime(naukri.lastSyncedAt)}</span>
                </>
              ) : (
                'Pull recommended India roles straight into your feed.'
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 pl-[52px] sm:pl-0">
          {naukri?.needsReconnect ? (
            <Button size="sm" onClick={() => setConnectOpen(true)}>
              Reconnect
            </Button>
          ) : naukri?.isConnected ? (
            <>
              <Button size="sm" variant="secondary" onClick={() => sync.mutate()} loading={sync.isPending}>
                Sync now
              </Button>
              <Button size="sm" variant="ghost" onClick={() => disconnect.mutate()} loading={disconnect.isPending}>
                Disconnect
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setConnectOpen(true)}>
              Connect
            </Button>
          )}
        </div>
      </div>
    ),
    'application-password': (
      <SettingRow
        label={settings.hasApplicationPassword ? 'Portal password saved' : 'No portal password yet'}
        hint="Used only mid-application, when a portal asks you to create an account; the same password on every portal so you can sign in there yourself later. Never shown, never shared."
      >
        <StatusPill tone={settings.hasApplicationPassword ? 'success' : 'warning'}>
          {settings.hasApplicationPassword ? 'Set' : 'Not set'}
        </StatusPill>
      </SettingRow>
    ),
  }

  return (
    <div className="max-w-[1080px]">
      <PageHeader eyebrow="Make it yours" title="Settings" description="Choose how applications are prepared, manage your email, and connect your job accounts." />

      <div className="lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-6">
        {/* Desktop: the anchor list, pinned, following the section in view. */}
        <nav aria-label="Settings sections" className="hidden lg:block">
          <div className="panel sticky top-[88px] p-3">
            <p className="label-eyebrow mb-2 px-3">On this page</p>
            <ul className="space-y-0.5">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    aria-current={active === section.id ? 'location' : undefined}
                    className={cn(
                      'relative block rounded-field px-3 py-2 text-[14px] transition-colors',
                      active === section.id
                        ? 'bg-accent-soft font-medium text-ink'
                        : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                    )}
                  >
                    {active === section.id && (
                      <span aria-hidden className="absolute -left-2 top-2 h-5 w-[3px] rounded-r bg-brand" />
                    )}
                    {section.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="min-w-0 space-y-3 md:space-y-5">
          {SECTIONS.map((section) => (
            <Section
              key={section.id}
              id={section.id}
              title={section.label}
              description={section.description}
              accordion={phone}
              open={!phone || open === section.id}
              onToggle={() => setOpen((current) => (current === section.id ? FIRST_SECTION : section.id))}
            >
              {bodies[section.id]}
            </Section>
          ))}
        </div>
      </div>

      <NaukriConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        reconnect={naukri?.needsReconnect}
      />
    </div>
  )
}

/** A number setting's input: mono digits, tall enough for a thumb. */
const NUMBER_INPUT = 'field-box num h-11 w-full sm:h-10 sm:w-24'

/**
 * One settings section. On a desktop a titled card; on a phone an accordion
 * item whose header opens and closes it.
 */
function Section({
  id,
  title,
  description,
  children,
  accordion,
  open,
  onToggle,
}: {
  id: string
  title: string
  description?: string
  children: ReactNode
  accordion: boolean
  open: boolean
  onToggle: () => void
}) {
  const bodyId = `${id}-body`
  const header = (
    <div className="min-w-0">
      <h2 className="text-[16px] font-semibold text-ink">{title}</h2>
      {description && <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">{description}</p>}
    </div>
  )
  return (
    <section id={id} className="card scroll-mt-[72px] overflow-hidden lg:scroll-mt-[96px]">
      {accordion ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className={cn(
            'flex min-h-[56px] w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted/60',
            open && 'border-b border-line',
          )}
        >
          {header}
          <svg
            className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform', open && 'rotate-180')}
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <header className="border-b border-line px-5 py-4">{header}</header>
      )}
      {open && (
        <div id={bodyId} className="divide-y divide-line px-4 py-1 sm:px-5">
          {children}
        </div>
      )}
    </section>
  )
}
